import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminAdjustCorporateUsageUsed,
  adminGetCorporateUsage,
  adminListCorporateSchools,
  adminSetCorporateUsageStatus,
  adminUpdateCorporateUsageAdminEmail,
  adminUpsertCorporateUsageCap,
} from "./adminApi";
import "./AdminDashboard.css";

const STATUS_OPTIONS = ["active", "inactive", "cancelled"];
const USAGE_TYPES = ["compo", "oral"];

function getUsagePayload(response) {
  return response?.data || response || {};
}

function getRows(payload) {
  return payload?.usage_rows || payload?.rows || [];
}

function getSchoolsPayload(response) {
  return response?.data?.schools || response?.schools || [];
}

function getSchoolId(school) {
  return school?.school_id || school?.RowKey || "";
}

function getSchoolLabel(school) {
  const id = getSchoolId(school);
  const name = school?.school_name || "";
  return name && id ? `${name} (${id})` : id;
}

function displayValue(value) {
  return value === 0 || value ? value : "-";
}

function remainingCredits(cap, used) {
  return (cap ?? 0) - (used ?? 0);
}

function parseInteger(value, label) {
  if (!String(value).trim()) {
    return { error: `${label} is required.` };
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    return { error: `${label} must be an integer.` };
  }

  return { value: parsed };
}

function parseOptionalNonNegativeInteger(value, label) {
  if (!String(value).trim()) {
    return { value: undefined };
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return { error: `${label} must be an integer greater than or equal to 0.` };
  }

  return { value: parsed };
}

function UsageSummary({ row }) {
  if (!row) {
    return <div className="admin-meta">No active subscription row found.</div>;
  }

  return (
    <div className="admin-summary-grid">
      <div className="admin-summary-item"><b>subscription_id:</b> {displayValue(row.subscription_id)}</div>
      <div className="admin-summary-item"><b>status:</b> {displayValue(row.status)}</div>
      <div className="admin-summary-item"><b>plan_id:</b> {displayValue(row.plan_id)}</div>
      <div className="admin-summary-item"><b>subscription_type:</b> {displayValue(row.subscription_type)}</div>
      <div className="admin-summary-item"><b>subscription_start_date:</b> {displayValue(row.subscription_start_date)}</div>
      <div className="admin-summary-item"><b>subscription_end_date:</b> {displayValue(row.subscription_end_date)}</div>
      <div className="admin-summary-item"><b>school_admin_email:</b> {displayValue(row.school_admin_email)}</div>
      <div className="admin-summary-item"><b>compo:</b> {displayValue(row.compo_credit_used)} / {displayValue(row.compo_credit_cap)} / {remainingCredits(row.compo_credit_cap, row.compo_credit_used)}</div>
      <div className="admin-summary-item"><b>oral:</b> {displayValue(row.oral_credit_used)} / {displayValue(row.oral_credit_cap)} / {remainingCredits(row.oral_credit_cap, row.oral_credit_used)}</div>
      <div className="admin-summary-item"><b>updated_at:</b> {displayValue(row.updated_at)}</div>
      <div className="admin-summary-item"><b>updated_by:</b> {displayValue(row.updated_by)}</div>
    </div>
  );
}

export default function AdminCorporateUsagePage() {
  const navigate = useNavigate();
  const [schoolId, setSchoolId] = useState("");
  const [currentSchoolId, setCurrentSchoolId] = useState("");
  const [activeUsage, setActiveUsage] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [compoCreditCap, setCompoCreditCap] = useState("");
  const [oralCreditCap, setOralCreditCap] = useState("");
  const [schoolAdminEmail, setSchoolAdminEmail] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [usageType, setUsageType] = useState("compo");
  const [delta, setDelta] = useState("");
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  function handleUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  }

  async function loadSchoolOptions() {
    setLoadingSchools(true);
    try {
      const response = await adminListCorporateSchools();
      const rows = getSchoolsPayload(response);
      setSchoolOptions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setSchoolOptions([]);
      setError(err.message || "Failed to load school list.");
    } finally {
      setLoadingSchools(false);
    }
  }

  useEffect(() => {
    loadSchoolOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectRow(row) {
    setSelectedRow(row);
    setCompoCreditCap(row?.compo_credit_cap ?? "");
    setOralCreditCap(row?.oral_credit_cap ?? "");
    setSchoolAdminEmail(row?.school_admin_email || "");
    setSelectedStatus(
      STATUS_OPTIONS.includes(row?.status) ? row.status : "active"
    );
    setDelta("");
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  async function loadUsage(targetSchoolId = currentSchoolId || schoolId) {
    const trimmedSchoolId = targetSchoolId.trim();

    if (!trimmedSchoolId) {
      setError("Please enter a school_id.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await adminGetCorporateUsage(trimmedSchoolId);
      const payload = getUsagePayload(response);
      const rows = getRows(payload);
      const active = payload?.active_usage || null;

      setCurrentSchoolId(trimmedSchoolId);
      setSchoolId(trimmedSchoolId);
      setActiveUsage(active);
      setUsageRows(rows);
      setSelectedRow(null);
      setSuccess("Usage loaded.");
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load corporate usage.");
      setActiveUsage(null);
      setUsageRows([]);
      setSelectedRow(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoadUsage(event) {
    event.preventDefault();
    await loadUsage(schoolId);
  }

  function requireSelectedRow() {
    if (!selectedRow?.subscription_id) {
      setError("Select a usage row first.");
      return false;
    }
    if (!currentSchoolId) {
      setError("Load a school first.");
      return false;
    }
    return true;
  }

  async function handleUpdateCaps() {
    if (!requireSelectedRow()) return;

    const compoParsed = parseOptionalNonNegativeInteger(
      compoCreditCap,
      "Compo credit cap"
    );
    if (compoParsed.error) {
      setError(compoParsed.error);
      return;
    }

    const oralParsed = parseOptionalNonNegativeInteger(
      oralCreditCap,
      "Oral credit cap"
    );
    if (oralParsed.error) {
      setError(oralParsed.error);
      return;
    }

    if (compoParsed.value === undefined && oralParsed.value === undefined) {
      setError("Enter at least one cap value to update.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpsertCorporateUsageCap({
        school_id: currentSchoolId,
        subscription_id: selectedRow.subscription_id,
        compo_credit_cap: compoParsed.value,
        oral_credit_cap: oralParsed.value,
        school_admin_email: schoolAdminEmail.trim(),
      });
      setSuccess("Caps updated.");
      await loadUsage(currentSchoolId);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to update caps.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateAdminEmail() {
    if (!requireSelectedRow()) return;

    if (!schoolAdminEmail.trim()) {
      setError("School admin email cannot be empty.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpdateCorporateUsageAdminEmail({
        school_id: currentSchoolId,
        subscription_id: selectedRow.subscription_id,
        school_admin_email: schoolAdminEmail.trim(),
      });
      setSuccess("Admin email updated.");
      await loadUsage(currentSchoolId);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to update admin email.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateStatus() {
    if (!requireSelectedRow()) return;

    if (!STATUS_OPTIONS.includes(selectedStatus)) {
      setError("Status must be active, inactive, or cancelled.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSetCorporateUsageStatus({
        school_id: currentSchoolId,
        subscription_id: selectedRow.subscription_id,
        status: selectedStatus,
      });
      setSuccess("Status updated.");
      await loadUsage(currentSchoolId);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to update status.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApplyAdjustment() {
    if (!requireSelectedRow()) return;

    if (!USAGE_TYPES.includes(usageType)) {
      setError("Usage type must be compo or oral.");
      return;
    }

    const parsedDelta = parseInteger(delta, "Delta");
    if (parsedDelta.error) {
      setError(parsedDelta.error);
      return;
    }

    const confirmed = window.confirm(
      `Apply usage adjustment of ${parsedDelta.value} to ${usageType} for subscription ${selectedRow.subscription_id}?`
    );
    if (!confirmed) return;

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminAdjustCorporateUsageUsed({
        school_id: currentSchoolId,
        subscription_id: selectedRow.subscription_id,
        usage_type: usageType,
        delta: parsedDelta.value,
      });
      setSuccess("Usage adjustment applied.");
      await loadUsage(currentSchoolId);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to adjust used credits.");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Corporate Usage</h2>
        <form onSubmit={handleLoadUsage}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>school_id</label>
              <select
                className="admin-input"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
                disabled={loadingSchools}
              >
                <option value="">
                  {loadingSchools ? "Loading schools..." : "Select school"}
                </option>
                {schoolOptions.map((school) => {
                  const id = getSchoolId(school);
                  if (!id) return null;
                  return (
                    <option key={id} value={id}>
                      {getSchoolLabel(school)}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={loading}
            >
              {loading ? "Loading..." : "Load Usage"}
            </button>
          </div>
        </form>

        {error && <div className="admin-error" style={{ marginTop: 12 }}>{error}</div>}
        {success && <div className="admin-success" style={{ marginTop: 12 }}>{success}</div>}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Active Subscription</h3>
        <UsageSummary row={activeUsage} />
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">All Usage Rows</h3>
        {usageRows.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  {[
                    "subscription_id",
                    "status",
                    "plan_id",
                    "start",
                    "end",
                    "school_admin_email",
                    "compo used/cap/remaining",
                    "oral used/cap/remaining",
                    "updated_at",
                    "",
                  ].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usageRows.map((row) => (
                  <tr key={`${row.subscription_id}-${row.status}-${row.updated_at}`}>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.subscription_id)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.status)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.plan_id)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.subscription_start_date)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.subscription_end_date)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.school_admin_email)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      {displayValue(row.compo_credit_used)} / {displayValue(row.compo_credit_cap)} / {remainingCredits(row.compo_credit_cap, row.compo_credit_used)}
                    </td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      {displayValue(row.oral_credit_used)} / {displayValue(row.oral_credit_cap)} / {remainingCredits(row.oral_credit_cap, row.oral_credit_used)}
                    </td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(row.updated_at)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        onClick={() => selectRow(row)}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-meta">No usage rows loaded.</div>
        )}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Edit Selected Row</h3>
        <div className="admin-summary-item" style={{ marginBottom: 12 }}>
          <b>subscription_id:</b> {selectedRow?.subscription_id || "-"}
        </div>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>compo_credit_cap</label>
            <input
              className="admin-input"
              value={compoCreditCap}
              onChange={(e) => setCompoCreditCap(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="admin-form-row">
            <label>oral_credit_cap</label>
            <input
              className="admin-input"
              value={oralCreditCap}
              onChange={(e) => setOralCreditCap(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="admin-form-row">
            <label>school_admin_email</label>
            <input
              className="admin-input"
              value={schoolAdminEmail}
              onChange={(e) => setSchoolAdminEmail(e.target.value)}
              placeholder="admin@example.com"
            />
          </div>
          <div className="admin-form-row">
            <label>status</label>
            <select
              className="admin-input"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={handleUpdateCaps} disabled={actionLoading}>
            Update Caps
          </button>
          <button type="button" className="admin-button admin-button-primary" onClick={handleUpdateAdminEmail} disabled={actionLoading}>
            Update Admin Email
          </button>
          <button type="button" className="admin-button admin-button-primary" onClick={handleUpdateStatus} disabled={actionLoading}>
            Update Status
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Adjust Used Credits</h3>
        <div className="admin-summary-item" style={{ marginBottom: 12 }}>
          <b>subscription_id:</b> {selectedRow?.subscription_id || "-"}
        </div>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>usage_type</label>
            <select
              className="admin-input"
              value={usageType}
              onChange={(e) => setUsageType(e.target.value)}
            >
              {USAGE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="admin-form-row">
            <label>delta</label>
            <input
              className="admin-input"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="e.g. 5 or -3"
            />
          </div>
        </div>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={handleApplyAdjustment} disabled={actionLoading}>
            Apply Adjustment
          </button>
        </div>
      </div>
    </div>
  );
}
